import { WritingAttempt, WritingTask, GradingResult } from '../types';
import { apiClient } from './apiClient';

export interface EssayDraft {
  id?: string;
  userId: string;
  taskId: string;
  taskType: 'task1' | 'task2';
  taskTitle: string;
  taskQuestion: string;
  essayContent: string;
  wordCount: number;
  timeSpent: number;
  lastSaved: string;
  createdAt: string;
  taskImageUrl?: string;
  taskImagePath?: string;
  taskImageMeta?: {
    width?: number;
    height?: number;
    sizeBytes?: number;
    mimeType?: string;
  };
  sourceMode?: 'ai-generated' | 'custom';
}

export interface EssayAttempt {
  id: string;
  user_id: string;
  task_id: string;
  task_type: 'task1' | 'task2';
  task_title: string;
  task_question: string;
  task_time_limit: number;
  task_word_count_min: number;
  task_word_count_max: number;
  essay_content: string;
  word_count: number;
  time_spent: number;
  is_completed: boolean;
  is_graded: boolean;
  overall_score?: number;
  task_response_score?: number;
  coherence_score?: number;
  lexical_score?: number;
  grammar_score?: number;
  task_response_feedback?: string;
  coherence_feedback?: string;
  lexical_feedback?: string;
  grammar_feedback?: string;
  overall_feedback?: string;
  suggestions?: string[];
  gradingResult?: GradingResult;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  task_image_url?: string;
  task_image_path?: string;
  task_image_meta?: {
    width?: number;
    height?: number;
    sizeBytes?: number;
    mimeType?: string;
    chartType?: string;
    chartData?: unknown;
  };
  task_chart_type?: string;
  task_chart_data?: unknown;
  source_mode?: 'ai-generated' | 'custom';
}


export interface IncompleteEssay {
  id: string;
  userId: string;
  taskId: string;
  taskType: 'task1' | 'task2';
  taskTitle: string;
  taskQuestion: string;
  taskTimeLimit: number;
  taskWordCountMin: number;
  taskWordCountMax: number;
  essayContent: string;
  wordCount: number;
  timeSpent: number;
  lastSaved: string;
  createdAt: string;
  updatedAt: string;
  taskImageUrl?: string;
  taskImagePath?: string;
  taskImageMeta?: {
    width?: number;
    height?: number;
    sizeBytes?: number;
    mimeType?: string;
    chartType?: string;
    chartData?: unknown;
  };
  taskChartType?: string;
  taskChartData?: unknown;
  sourceMode?: 'ai-generated' | 'custom';
}

class EssayService {
  private draftSaveFailures = new Map<string, { consecutiveFailures: number; nextRetryAt: number }>();

  private isDebugEnabled(): boolean {
    if (process.env.NODE_ENV !== 'development') return false;
    try {
      return window.localStorage.getItem('debug_essay_service') === 'true';
    } catch {
      return false;
    }
  }

  private debugLog(message: string, data?: unknown) {
    if (!this.isDebugEnabled()) return;
    if (data === undefined) {
      // eslint-disable-next-line no-console
      console.info(message);
      return;
    }
    // eslint-disable-next-line no-console
    console.info(message, data);
  }

  private normalizeResult(rawResult: any, attempt?: EssayAttempt): GradingResult | null {
    if (!rawResult && !attempt?.overall_score) return null;

    const feedback = rawResult?.feedback || rawResult?.feedbacks || rawResult?.detailedFeedback || rawResult?.details || {};
    const scores = rawResult?.scores || rawResult?.bandScores || rawResult?.components || {};

    const overallScore = Number(
      rawResult?.overallScore ??
      rawResult?.overall_score ??
      rawResult?.bandScore ??
      rawResult?.band_score ??
      attempt?.overall_score ??
      0
    );

    const taskAchievementScore = Number(
      scores?.taskAchievement ??
      scores?.task_achievement ??
      scores?.taskResponse ??
      scores?.task_response ??
      rawResult?.taskAchievement ??
      rawResult?.task_achievement ??
      rawResult?.taskResponse ??
      rawResult?.task_response ??
      attempt?.task_response_score ??
      0
    );

    const taskResponseScore = Number(
      scores?.taskResponse ??
      scores?.task_response ??
      scores?.taskAchievement ??
      scores?.task_achievement ??
      rawResult?.taskResponse ??
      rawResult?.task_response ??
      rawResult?.taskAchievement ??
      rawResult?.task_achievement ??
      attempt?.task_response_score ??
      0
    );

    const coherenceScore = Number(scores?.coherence ?? scores?.coherence_score ?? rawResult?.coherence ?? attempt?.coherence_score ?? 0);
    const lexicalScore = Number(
      scores?.lexical ?? scores?.lexicalResource ?? scores?.lexical_resource ?? rawResult?.lexical ?? rawResult?.lexicalResource ?? attempt?.lexical_score ?? 0
    );
    const grammarScore = Number(scores?.grammar ?? scores?.grammarAccuracy ?? scores?.grammar_accuracy ?? rawResult?.grammar ?? attempt?.grammar_score ?? 0);

    const suggestionsSource =
      rawResult?.suggestions ??
      rawResult?.recommendations ??
      rawResult?.improvements ??
      attempt?.suggestions ??
      [];
    const suggestions = Array.isArray(suggestionsSource)
      ? suggestionsSource.map((item: unknown) => String(item)).filter(Boolean)
      : [];

    const normalized: GradingResult = {
      overallScore,
      scores: {
        taskAchievement: taskAchievementScore,
        taskResponse: taskResponseScore,
        coherence: coherenceScore,
        lexical: lexicalScore,
        grammar: grammarScore,
      },
      feedback: {
        taskAchievement: String(
          feedback?.taskAchievement ??
          feedback?.task_achievement ??
          feedback?.taskResponse ??
          feedback?.task_response ??
          rawResult?.taskAchievementFeedback ??
          rawResult?.task_achievement_feedback ??
          rawResult?.taskResponseFeedback ??
          rawResult?.task_response_feedback ??
          attempt?.task_response_feedback ??
          feedback?.task ??
          'No feedback available'
        ),
        taskResponse: String(
          feedback?.taskResponse ??
          feedback?.task_response ??
          feedback?.taskAchievement ??
          feedback?.task_achievement ??
          rawResult?.taskResponseFeedback ??
          rawResult?.task_response_feedback ??
          rawResult?.taskAchievementFeedback ??
          rawResult?.task_achievement_feedback ??
          attempt?.task_response_feedback ??
          feedback?.task ??
          'No feedback available'
        ),
        coherence: String(
          feedback?.coherence ??
          feedback?.coherence_cohesion ??
          rawResult?.coherenceFeedback ??
          rawResult?.coherence_feedback ??
          attempt?.coherence_feedback ??
          'No feedback available'
        ),
        lexical: String(
          feedback?.lexical ??
          feedback?.lexical_resource ??
          rawResult?.lexicalFeedback ??
          rawResult?.lexical_feedback ??
          attempt?.lexical_feedback ??
          'No feedback available'
        ),
        grammar: String(
          feedback?.grammar ??
          feedback?.grammar_accuracy ??
          rawResult?.grammarFeedback ??
          rawResult?.grammar_feedback ??
          attempt?.grammar_feedback ??
          'No feedback available'
        ),
        overall: String(
          feedback?.overall ??
          feedback?.overall_feedback ??
          rawResult?.overallFeedback ??
          rawResult?.overall_feedback ??
          attempt?.overall_feedback ??
          rawResult?.summary ??
          'No feedback available'
        ),
      },
      suggestions,
    };

    return normalized;
  }

  // No baseURL needed - using apiClient

  /**
   * Get all essay attempts for a user
   */
  async getAttempts(userId: string, limit?: number, offset?: number): Promise<EssayAttempt[]> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());

      const data = await apiClient.get<{ attempts: EssayAttempt[] }>(`/essays/attempts/${userId}?${params}`);
      this.debugLog('Retrieved essay attempts', {
        userId,
        count: data.attempts?.length || 0,
        data: data.attempts?.map(a => ({
          id: a.id,
          taskTitle: a.task_title,
          overallScore: a.overall_score,
          isCompleted: a.is_completed,
          isGraded: a.is_graded
        }))
      });
      return data.attempts || [];
    } catch (error) {
      console.error('Error fetching essay attempts:', error);
      throw error;
    }
  }

  /**
   * Get incomplete essays for a user
   */
  async getIncompleteEssays(userId: string): Promise<IncompleteEssay[]> {
    try {
      const data = await apiClient.get<{ essays: any[] }>(`/essays/incomplete/${userId}`);
      
      // Convert snake_case API response to camelCase IncompleteEssay format
      const convertedEssays = (data.essays || []).map(essay => ({
        id: essay.id,
        userId: essay.user_id || essay.userId,
        taskId: essay.task_id || essay.taskId,
        taskType: essay.task_type || essay.taskType,
        taskTitle: essay.task_title || essay.taskTitle,
        taskQuestion: essay.task_question || essay.taskQuestion,
        taskTimeLimit: essay.task_time_limit || essay.taskTimeLimit || 40,
        taskWordCountMin: essay.task_word_count_min || essay.taskWordCountMin || 250,
        taskWordCountMax: essay.task_word_count_max || essay.taskWordCountMax || 400,
        taskImageUrl: essay.task_image_url || essay.taskImageUrl,
        taskImagePath: essay.task_image_path || essay.taskImagePath,
        taskImageMeta: essay.task_image_meta || essay.taskImageMeta,
        taskChartType: essay.task_chart_type || essay.taskChartType,
        taskChartData: essay.task_chart_data || essay.taskChartData,
        sourceMode: essay.source_mode || essay.sourceMode,
        essayContent: essay.essay_content || essay.essayContent || '',
        wordCount: essay.word_count || essay.wordCount || 0,
        timeSpent: essay.time_spent || essay.timeSpent || 0,
        lastSaved: essay.updated_at || essay.updatedAt || essay.lastSaved,
        createdAt: essay.created_at || essay.createdAt,
        updatedAt: essay.updated_at || essay.updatedAt
      }));
      
      this.debugLog('Retrieved incomplete essays', {
        userId,
        count: convertedEssays.length,
        data: convertedEssays.map(e => ({
          id: e.id,
          taskTitle: e.taskTitle,
          wordCount: e.wordCount,
          timeSpent: e.timeSpent
        }))
      });
      return convertedEssays;
    } catch (error) {
      console.error('Error fetching incomplete essays', error);
      throw error;
    }
  }

  /**
   * Clear all incomplete essays for a user
   */
  async clearIncompleteEssays(userId: string): Promise<boolean> {
    try {
      await apiClient.delete(`/essays/incomplete/clear`);
      this.debugLog('Cleared incomplete essays for user', { userId });
      return true;
    } catch (error) {
      console.error('Error clearing incomplete essays', error);
      return false;
    }
  }

  /**
   * Create a new essay attempt
   */
  async createAttempt(attempt: Omit<EssayAttempt, 'id' | 'created_at' | 'updated_at'>): Promise<string | null> {
    try {
      this.debugLog('Creating essay attempt', {
        taskId: attempt.task_id,
        taskType: attempt.task_type,
        taskTitle: attempt.task_title,
        wordCount: attempt.word_count,
        isCompleted: attempt.is_completed,
        isGraded: attempt.is_graded,
        timestamp: new Date().toISOString()
      });

      const data = await apiClient.post<{ id: string }>('/essays/attempt', attempt);
      
      this.debugLog('Essay attempt created successfully', {
        attemptId: data.id,
        timestamp: new Date().toISOString()
      });
      
      return data.id || null;
    } catch (error) {
      console.error('❌ Error creating essay attempt:', {
        error: error instanceof Error ? error.message : String(error),
        attempt: {
          taskId: attempt.task_id,
          taskType: attempt.task_type,
          taskTitle: attempt.task_title
        },
        timestamp: new Date().toISOString()
      });
      return null;
    }
  }

  /**
   * Update an essay attempt
   */
  async updateAttempt(attemptId: string, updates: Partial<EssayAttempt>): Promise<boolean> {
    try {
      await apiClient.put(`/essays/attempt/${attemptId}`, updates);
      return true;
    } catch (error) {
      console.error('Error updating essay attempt:', error);
      return false;
    }
  }

  /**
   * Delete an essay attempt
   */
  async deleteAttempt(attemptId: string): Promise<boolean> {
    try {
      await apiClient.delete(`/essays/attempt/${attemptId}`);
      return true;
    } catch (error) {
      console.error('Error deleting essay attempt:', error);
      return false;
    }
  }

  /**
   * Clear all essay attempts for a user
   */
  async clearAllAttempts(userId: string): Promise<boolean> {
    try {
      await apiClient.delete(`/essays/attempts/${userId}`);
      return true;
    } catch (error) {
      console.error('Error clearing all attempts:', error);
      return false;
    }
  }

  /**
   * Tester-only full data clear (attempts + drafts) on server.
   */
  async clearAllData(): Promise<boolean> {
    try {
      await apiClient.delete('/account/clear-all-data');
      return true;
    } catch (error) {
      console.error('Error clearing all data:', error);
      return false;
    }
  }

  /**
   * Grade an essay
   */
  async gradeEssay(essayContent: string, taskType: 'task1' | 'task2'): Promise<GradingResult | null> {
    try {
      const data = await apiClient.post<{ grading: GradingResult }>('/grade-essay', {
        essayContent,
        taskType
      });
      return data.grading || null;
    } catch (error) {
      console.error('Error grading essay:', error);
      return null;
    }
  }

  /**
   * Generate AI hints for an essay
   */
  async generateHints(essayContent: string, taskType: 'task1' | 'task2'): Promise<string[] | null> {
    try {
      const data = await apiClient.post<{ hints: string[] }>('/generate-hints', {
        essayContent,
        taskType
      });
      return data.hints || null;
    } catch (error) {
      console.error('Error generating hints:', error);
      return null;
    }
  }

  /**
   * Rewrite an essay with AI assistance
   */
  async rewriteEssay(essayContent: string, taskType: 'task1' | 'task2'): Promise<string | null> {
    try {
      const data = await apiClient.post<{ rewrittenEssay: string }>('/rewrite-essay', {
        essayContent,
        taskType
      });
      return data.rewrittenEssay || null;
    } catch (error) {
      console.error('Error rewriting essay:', error);
      return null;
    }
  }

  /**
   * Generate a chart for essay analytics
   */
  async generateChart(chartType: string, data: any): Promise<string | null> {
    try {
      const response = await apiClient.post<{ chartUrl: string }>('/generate-chart', {
        chartType,
        data
      });
      return response.chartUrl || null;
    } catch (error) {
      console.error('Error generating chart:', error);
      return null;
    }
  }

  /**
   * Get writing tasks
   */
  async getWritingTasks(): Promise<WritingTask[]> {
    // This would typically come from an API, but for now we'll return mock data
    return [
      {
        id: 'task1-1',
        type: 'task1',
        title: 'IELTS Writing Task 1 - Line Graph',
        question: 'The graph below shows the number of overseas visitors to three different areas in a European country between 1987 and 2007. Summarize the information by selecting and reporting the main features, and make comparisons where relevant.',
        timeLimit: 20,
        wordCount: {
          min: 150,
          max: 200
        },
        chartType: 'Line graph'
      },
      {
        id: 'task2-1',
        type: 'task2',
        title: 'IELTS Writing Task 2 - Opinion Essay',
        question: 'Some people believe that technology has made our lives more complicated, while others think it has made our lives easier. Discuss both views and give your own opinion.',
        timeLimit: 40,
        wordCount: {
          min: 250,
          max: 300
        },
        topic: 'Technology'
      }
    ];
  }

  /**
   * Save essay draft to localStorage and server
   */
  async saveDraft(draft: EssayDraft): Promise<void> {
    try {
      this.debugLog('Saving essay draft', {
        draftId: draft.id,
        taskId: draft.taskId,
        taskType: draft.taskType,
        wordCount: draft.wordCount,
        essayLength: draft.essayContent.length,
        timestamp: new Date().toISOString()
      });

      // Save to localStorage first (immediate)
      const drafts = this.getDrafts();
      const existingIndex = drafts.findIndex(d => d.id === draft.id);
      
      if (existingIndex >= 0) {
        drafts[existingIndex] = { ...draft, lastSaved: new Date().toISOString() };
        this.debugLog('Updated existing draft in localStorage');
      } else {
        drafts.push({ ...draft, lastSaved: new Date().toISOString() });
        this.debugLog('Added new draft to localStorage');
      }
      
      localStorage.setItem('essay_drafts', JSON.stringify(drafts));
      this.debugLog('Draft saved to localStorage successfully');
      
      // Save to server (async, don't block UI)
      try {
        const draftFailureState = this.draftSaveFailures.get(draft.id || draft.taskId);
        const now = Date.now();
        if (draftFailureState && draftFailureState.nextRetryAt > now) {
          this.debugLog('Skipping server draft save due to cooldown', {
            draftId: draft.id,
            nextRetryAt: new Date(draftFailureState.nextRetryAt).toISOString()
          });
          return;
        }

        this.debugLog('Saving draft to server', {
          taskId: draft.taskId,
          taskType: draft.taskType,
          wordCount: draft.wordCount
        });

        await apiClient.post('/essays/draft', {
          task_id: draft.taskId,
          task_type: draft.taskType,
          task_title: draft.taskTitle,
          task_question: draft.taskQuestion,
          essay_content: draft.essayContent,
          word_count: draft.wordCount,
          task_image_url: draft.taskImageUrl,
          task_image_path: draft.taskImagePath,
          task_image_meta: draft.taskImageMeta,
          source_mode: draft.sourceMode
        });

        this.draftSaveFailures.delete(draft.id || draft.taskId);
        this.debugLog('Draft saved to server successfully');
      } catch (serverError) {
        const failureKey = draft.id || draft.taskId;
        const prev = this.draftSaveFailures.get(failureKey);
        const consecutiveFailures = (prev?.consecutiveFailures || 0) + 1;
        const cooldownMs = Math.min(60000, Math.pow(2, Math.min(consecutiveFailures, 5)) * 1000); // 2s..60s
        this.draftSaveFailures.set(failureKey, {
          consecutiveFailures,
          nextRetryAt: Date.now() + cooldownMs
        });

        console.error('❌ Failed to save draft to server:', {
          error: serverError instanceof Error ? serverError.message : String(serverError),
          draftId: draft.id,
          taskId: draft.taskId,
          consecutiveFailures,
          nextRetryInMs: cooldownMs,
          timestamp: new Date().toISOString()
        });
        // Don't throw - localStorage backup is available
      }
    } catch (error) {
      console.error('💥 Error saving draft:', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        draftId: draft.id,
        taskId: draft.taskId,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  }

  /**
   * Get essay drafts from localStorage
   */
  getDrafts(): EssayDraft[] {
    try {
      const drafts = localStorage.getItem('essay_drafts');
      return drafts ? JSON.parse(drafts) : [];
    } catch (error) {
      console.error('Error getting drafts:', error);
      return [];
    }
  }

  /**
   * Delete essay draft from localStorage
   */
  deleteDraft(draftId: string): void {
    try {
      const drafts = this.getDrafts();
      const filteredDrafts = drafts.filter(d => d.id !== draftId);
      localStorage.setItem('essay_drafts', JSON.stringify(filteredDrafts));
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  }

  /**
   * Clear all drafts from localStorage
   */
  clearDrafts(): void {
    try {
      localStorage.removeItem('essay_drafts');
    } catch (error) {
      console.error('Error clearing drafts:', error);
    }
  }

  /**
   * Calculate word count for essay content
   */
  calculateWordCount(content: string): number {
    if (!content || content.trim() === '') return 0;
    return content.trim().split(/\s+/).length;
  }

  /**
   * Validate essay content
   */
  validateEssay(content: string, minWords: number, maxWords: number): { valid: boolean; message?: string } {
    const wordCount = this.calculateWordCount(content);
    
    if (wordCount < minWords) {
      return { valid: false, message: `Essay must be at least ${minWords} words. Current: ${wordCount} words.` };
    }
    
    if (wordCount > maxWords) {
      return { valid: false, message: `Essay should not exceed ${maxWords} words. Current: ${wordCount} words.` };
    }
    
    return { valid: true };
  }

  /**
   * Format time in MM:SS format
   */
  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Calculate time remaining
   */
  calculateTimeRemaining(timeSpent: number, timeLimit: number): number {
    return Math.max(0, timeLimit - timeSpent);
  }

  /**
   * Check if time is up
   */
  isTimeUp(timeSpent: number, timeLimit: number): boolean {
    return timeSpent >= timeLimit;
  }

  /**
   * Get progress percentage
   */
  getProgressPercentage(wordCount: number, minWords: number, maxWords: number): number {
    if (wordCount >= maxWords) return 100;
    return Math.min(100, (wordCount / minWords) * 100);
  }

  /**
   * Get writing speed (words per minute)
   */
  getWritingSpeed(wordCount: number, timeSpent: number): number {
    if (timeSpent === 0) return 0;
    return Math.round((wordCount / timeSpent) * 60);
  }

  /**
   * Convert WritingAttempt to database format
   */
  convertFromWritingAttempt(attempt: WritingAttempt, userId: string): Omit<EssayAttempt, 'id' | 'created_at' | 'updated_at'> {
    const taskImageMeta = { ...(attempt.task.imageMeta || {}) } as Record<string, unknown>;
    if (attempt.task.chartData) {
      taskImageMeta.chartType = attempt.task.chartType || attempt.task.chartData.type;
      taskImageMeta.chartData = attempt.task.chartData;
    }
    const normalizedTaskImageMeta = Object.keys(taskImageMeta).length > 0 ? taskImageMeta : undefined;

    return {
      user_id: userId,
      task_id: attempt.task.id,
      task_type: attempt.task.type,
      task_title: attempt.task.title,
      task_question: attempt.task.question,
      task_image_url: attempt.task.imageUrl,
      task_image_path: attempt.task.imagePath,
      task_image_meta: normalizedTaskImageMeta,
      source_mode: attempt.task.sourceMode,
      task_time_limit: attempt.task.timeLimit,
      task_word_count_min: attempt.task.wordCount.min,
      task_word_count_max: attempt.task.wordCount.max,
      essay_content: attempt.essay,
      word_count: attempt.wordCount,
      time_spent: attempt.timeSpent,
      is_completed: true, // WritingAttempt represents completed attempts
      is_graded: !!attempt.result,
      overall_score: attempt.result?.overallScore,
      task_response_score: attempt.result?.scores?.taskResponse,
      coherence_score: attempt.result?.scores?.coherence,
      lexical_score: attempt.result?.scores?.lexical,
      grammar_score: attempt.result?.scores?.grammar,
      task_response_feedback: attempt.result?.feedback?.taskResponse || attempt.result?.feedback?.taskAchievement,
      coherence_feedback: attempt.result?.feedback?.coherence,
      lexical_feedback: attempt.result?.feedback?.lexical,
      grammar_feedback: attempt.result?.feedback?.grammar,
      overall_feedback: attempt.result?.feedback?.overall,
      suggestions: attempt.result?.suggestions || [],
      gradingResult: attempt.result,
      completed_at: attempt.timestamp
    };
  }

  /**
   * Save essay attempt to database
   */
  async saveAttempt(attempt: Omit<EssayAttempt, 'id' | 'created_at' | 'updated_at'>): Promise<string | null> {
    return this.createAttempt(attempt);
  }

  /**
   * Convert EssayAttempt to WritingAttempt format
   */
  convertToWritingAttempt(attempt: EssayAttempt): WritingAttempt {
    const result = this.normalizeResult(attempt.gradingResult, attempt);
    const taskImageMeta = attempt.task_image_meta && typeof attempt.task_image_meta === 'object'
      ? attempt.task_image_meta as Record<string, unknown>
      : null;
    const restoredChartData = (attempt.task_chart_data || taskImageMeta?.chartData || undefined) as WritingTask['chartData'];
    const restoredChartType = (
      attempt.task_chart_type ||
      (typeof taskImageMeta?.chartType === 'string' ? taskImageMeta.chartType : undefined) ||
      restoredChartData?.type
    );

    return {
      id: attempt.id,
      task: {
        id: attempt.task_id,
        type: attempt.task_type,
        title: attempt.task_title,
        question: attempt.task_question,
        imageUrl: attempt.task_image_url,
        imagePath: attempt.task_image_path,
        imageMeta: attempt.task_image_meta,
        chartType: restoredChartType,
        chartData: restoredChartData,
        sourceMode: attempt.source_mode,
        timeLimit: attempt.task_time_limit,
        wordCount: {
          min: attempt.task_word_count_min,
          max: attempt.task_word_count_max
        }
      },
      essay: attempt.essay_content,
      wordCount: attempt.word_count,
      timeSpent: attempt.time_spent,
      result: result || {
        overallScore: 0,
        scores: {
          taskAchievement: 0,
          taskResponse: 0,
          coherence: 0,
          lexical: 0,
          grammar: 0
        },
        feedback: {
          taskAchievement: 'No feedback available',
          taskResponse: 'No feedback available',
          coherence: 'No feedback available',
          lexical: 'No feedback available',
          grammar: 'No feedback available',
          overall: 'No feedback available'
        },
        suggestions: []
      },
      timestamp: attempt.completed_at || attempt.created_at
    };
  }

  /**
   * Convert IncompleteEssay to WritingAttempt format
   */
  convertIncompleteToWritingAttempt(attempt: IncompleteEssay): WritingAttempt {
    const taskImageMeta = attempt.taskImageMeta && typeof attempt.taskImageMeta === 'object'
      ? attempt.taskImageMeta as Record<string, unknown>
      : null;
    const restoredChartData = (attempt.taskChartData || taskImageMeta?.chartData || undefined) as WritingTask['chartData'];
    const restoredChartType = (
      (typeof attempt.taskChartType === 'string' ? attempt.taskChartType : undefined) ||
      (typeof taskImageMeta?.chartType === 'string' ? taskImageMeta.chartType : undefined) ||
      restoredChartData?.type
    );

    return {
      id: attempt.id,
      task: {
        id: attempt.taskId,
        type: attempt.taskType,
        title: attempt.taskTitle,
        question: attempt.taskQuestion,
        imageUrl: attempt.taskImageUrl,
        imagePath: attempt.taskImagePath,
        imageMeta: attempt.taskImageMeta,
        chartType: restoredChartType,
        chartData: restoredChartData,
        sourceMode: attempt.sourceMode,
        timeLimit: attempt.taskTimeLimit,
        wordCount: {
          min: attempt.taskWordCountMin,
          max: attempt.taskWordCountMax
        }
      },
      essay: attempt.essayContent,
      wordCount: attempt.wordCount,
      timeSpent: attempt.timeSpent,
      result: {
        overallScore: 0,
        scores: {
          coherence: 0,
          lexical: 0,
          grammar: 0
        },
        feedback: {
          coherence: 'No feedback available',
          lexical: 'No feedback available',
          grammar: 'No feedback available',
          overall: 'No feedback available'
        },
        suggestions: []
      },
      timestamp: attempt.createdAt
    };
  }

  /**
   * Estimate remaining time based on writing speed
   */
  estimateRemainingTime(wordCount: number, timeSpent: number, targetWords: number): number {
    const writingSpeed = this.getWritingSpeed(wordCount, timeSpent);
    if (writingSpeed === 0) return 0;
    
    const remainingWords = Math.max(0, targetWords - wordCount);
    return Math.round((remainingWords / writingSpeed) * 60);
  }

  /**
   * Generate writing tips based on current progress
   */
  getWritingTips(wordCount: number, timeSpent: number, timeLimit: number, minWords: number): string[] {
    const tips: string[] = [];
    const timeRemaining = this.calculateTimeRemaining(timeSpent, timeLimit);
    const writingSpeed = this.getWritingSpeed(wordCount, timeSpent);
    
    if (timeRemaining < 300 && wordCount < minWords * 0.8) {
      tips.push('Time is running out! Focus on completing your main points.');
    }
    
    if (writingSpeed < 10) {
      tips.push('Try to write faster. Aim for at least 10 words per minute.');
    }
    
    if (wordCount < minWords * 0.5) {
      tips.push('You need to write more content to meet the minimum word requirement.');
    }
    
    if (timeSpent > timeLimit * 0.8 && wordCount < minWords) {
      tips.push('Consider summarizing your points to finish within the time limit.');
    }
    
    return tips;
  }
}

export const essayService = new EssayService();
